import type { FC } from 'react';
import React from 'react';
import { IconButton, Stack, styled } from '@mui/material';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { Iconify, Tooltip } from '@pflegenavi/web-components';
import { NavigationBarImagePreview } from './NavigationBarImagePreview';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

interface ImagesPreviewNavigationBarProps {
  imageUrls: string[];
  imageUrlToTypeMap: UrlTypeNameMapping;
  selectedImageUrl: string;
  onSelectDifferentImage: (imageUrl: string) => void;
  onDeleteImage?: (index: number) => void;
  onAddImage?: () => void;
}

export const ImagesPreviewNavigationBar: FC<
  ImagesPreviewNavigationBarProps
> = ({
  selectedImageUrl,
  onSelectDifferentImage,
  imageUrls,
  imageUrlToTypeMap,
  onDeleteImage,
  onAddImage,
}) => {
  const { t } = useTranslation();

  return (
    // Side-scrolling instead of paged four at a time (ENG-2295): the page
    // arrows and the separate upload button cost two rows of vertical space
    // that the inline PDF preview needs more than the rare 5+ receipt case.
    <Stack
      direction="row"
      alignItems="center"
      sx={{ flex: 1, minWidth: 0, overflowX: 'auto' }}
    >
      {imageUrls.map((imageUrl, index) => {
        const file = imageUrlToTypeMap.get(imageUrl);

        const isLoading = file === undefined;
        const isPdf = file?.type === 'application/pdf';
        const isImage = file?.type?.startsWith('image/') ?? false;

        if (!isLoading && !isPdf && !isImage) {
          // eslint-disable-next-line no-console
          console.warn(
            `File with content-type ${file.type} is not supported: ${imageUrl}`
          );
          Sentry.captureMessage(
            `File with content-type ${file.type} is not supported: ${imageUrl}`,
            'warning'
          );
        }

        return (
          <NavigationBarImagePreview
            onImageSelect={onSelectDifferentImage}
            imageUrl={imageUrl}
            fileName={file?.name ?? 'unknown'}
            isLoading={isLoading}
            isPdf={isPdf}
            isImage={isImage}
            key={imageUrl}
            selected={selectedImageUrl === imageUrl}
            onDelete={onDeleteImage && (() => onDeleteImage(index))}
          />
        );
      })}
      {onAddImage && (
        <Tooltip title={t('actions.upload-additional-receipt')} placement="top">
          <AddImageButton onClick={onAddImage}>
            <Iconify icon="eva:plus-fill" fontSize={28} />
          </AddImageButton>
        </Tooltip>
      )}
    </Stack>
  );
};

const AddImageButton = styled(IconButton)(({ theme }) =>
  theme.unstable_sx({
    flexShrink: 0,
    height: 60,
    width: 60,
    margin: 0.5,
    borderRadius: 2,
    color: theme.palette.grey['500'],
  })
);
